/**
 * Solanaland
 *
 * Author: Phia Nasol
 * Company: Digiteer Inc.
 *
 * https://www.digiteer.digital/
 *
 */

import React from "react";
import { Col, Container, Modal, Row, Spinner } from "react-bootstrap";
import { Tab, Tabs, TabList, TabPanel } from "react-tabs";
import { Controller, useForm } from "react-hook-form";
import ReactSelect from "react-select";
import { api, ApiError } from "core/api";
import { Alert, Button, Card, FieldGroup, Input, Select, SvgIcon, Table } from "core/components";
import formatter from "core/formatter";
import { navigateTo } from "core/navigation";
import moment from "moment";
import { admin_purchase_orders_path, purchase_requests_admin_material_reordering_index_path } from "routes";
import { IMaskInput } from "react-imask";
import MaterialReorderingRequestHistory from "./MaterialReorderingRequestHistory";

const MaterialReorderingRequests = ({ warehouses, has_purchase_request_history }): JSX.Element => {
  const [loading, setLoading] = React.useState(false);
  const [purchaseRequestItems, setPurchaseRequestItems] = React.useState([]);
  const [tabIndex, setTabIndex] = React.useState(0);
  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    formState: { isSubmitting, isValidating }
  } = useForm({
    mode: "onChange"
  });
  const [formErrors, setFormErrors] = React.useState<Array<string>>([]);
  const table = React.useRef(null);
  const [showPurchaseOrdersModal, setShowPurchaseOrdersModal] = React.useState(false);
  const [selectedPurchaseRequest, setSelectedPurchaseRequest] = React.useState(null);
  const [purchaseOrders, setPurchaseOrders] = React.useState([]);
  const [warehouseFilter, setWarehouseFilter] = React.useState("");

  const aggregateValue = (leafValues) => {
    return leafValues[0];
  };

  const aggregateSum = (leafValues) => {
    const reducer = (accumulator, currrent) => parseFloat(accumulator) + parseFloat(currrent);

    return leafValues.reduce(reducer);
  };

  const save = async (data) => {
    const purchaseRequests = data.data.filter((datum) => datum.purchase_request_item_id);

    try {
      if (purchaseRequests.length > 0) {
        await api.post(admin_purchase_orders_path(), {
          purchase_requests: purchaseRequests
        });
        navigateTo(admin_purchase_orders_path());
      }
    } catch (error) {
      if (error instanceof ApiError) {
        setFormErrors(error.all());
      } else {
        setFormErrors([error?.message]);
      }
    }
  };

  const onError = (data) => {
    if (data?.data) {
      setFormErrors(["There are errors on your form."]);
    }
  };

  const columns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: 1,
        Header: "Purchase Request",
        accessor: "purchase_request.code"
      },
      {
        id: "item.code",
        Header: "Inventory ID",
        accessor: "item.code",
        style: {
          width: "10%"
        },
        Cell: (props) => {
          return (
            <div title={`${props.value} (${props.row.subRows.length})`} data-bs-toggle="tooltip">
              <p className="text-center text-bold text-truncate">{`${props.value} (${props.row.subRows.length})`}</p>
            </div>
          );
        }
      },
      {
        id: 2,
        Header: "Inventory",
        accessor: "item.name",
        style: {
          width: "13%"
        },
        aggregate: aggregateValue,
        Aggregated: ({ value }) => (
          <div title={value} data-bs-toggle="tooltip">
            <p className="text-truncate text-bold">{value}</p>
          </div>
        ),
        Cell: (props) => {
          const warehouse = props.row.original.warehouse;

          return (
            <div title={warehouse.name} data-bs-toggle="tooltip">
              <p className="text-truncate">{warehouse.name}</p>
            </div>
          );
        }
      },
      {
        id: 3,
        Header: "Warehouse",
        accessor: "warehouse.name",
        Cell: () => {
          return <></>;
        }
      },
      {
        id: 4,
        Header: "Warehouse",
        accessor: "warehouse.id",
        Cell: () => {
          return <></>;
        }
      },
      {
        Header: "P.R Info",
        accessor: "purchase_request.code",
        Cell: (props) => {
          const purchaseRequest = props.row.original?.purchase_request;

          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
              <p className="text-center text-truncate">{purchaseRequest?.created_by}</p>
            </div>
          );
        }
      },
      {
        Header: "Requested Quantity",
        accessor: "requested_quantity",
        aggregate: aggregateSum,
        Aggregated: ({ row }) => {
          const leafRows = row.leafRows;
          const leafTotalAmounts = leafRows.map((leafRow) => {
            const totalAmount = watch(`data.${leafRow.id}.requested_quantity`);
            return totalAmount ? totalAmount : "0.0";
          });
          const aggregatedTotal = aggregateSum(leafTotalAmounts);

          return aggregatedTotal ? (
            <div title={parseFloat(aggregatedTotal).toLocaleString("en-US", { minimumFractionDigits: 2 })} data-bs-toggle="tooltip">
              <p className="text-right text-bold text-truncate">
                {parseFloat(aggregatedTotal).toLocaleString("en-US", { minimumFractionDigits: 2 })}
              </p>
            </div>
          ) : (
            <p className="text-right text-bold text-truncate">{"0.0"}</p>
          );
        },
        style: {
          textAlign: "center"
        },
        Cell: (props) => {
          return (
            <Controller
              name={`data.${props.row.index}.requested_quantity`}
              control={control}
              render={({ field }) => (
                <FieldGroup>
                  <IMaskInput
                    mask={Number}
                    padFractionalZeros={true}
                    thousandsSeparator=","
                    radix="."
                    value={field.value?.toString()}
                    unmask={true}
                    inputRef={field.ref}
                    onAccept={(value) => {
                      field.onChange(value);
                      if (value) {
                        const unitPrice = parseFloat(getValues(`data.${props.row.index}.unit_price`));
                        if (unitPrice) {
                          const requestedQuantity = parseFloat(value);
                          const totalAmount = requestedQuantity * unitPrice;
                          setValue(`data.${props.row.index}.total_amount`, totalAmount.toString());
                        }
                      } else {
                        setValue(`data.${props.row.index}.total_amount`, null);
                      }
                    }}
                    className="field"
                    placeholder="0.00"
                    min={0}
                    max={9999999}
                  />
                </FieldGroup>
              )}
            />
          );
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        aggregate: aggregateSum,
        Aggregated: ({ value }) => (
          <div title={value} data-bs-toggle="tooltip">
            <p className="text-center text-bold">{value}</p>
          </div>
        ),
        style: {
          textAlign: "center"
        },
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Remaining Quantity",
        accessor: "remaining_quantity",
        aggregate: aggregateSum,
        Aggregated: ({ value }) => (
          <div title={value} data-bs-toggle="tooltip">
            <p className="text-center text-bold">{value}</p>
          </div>
        ),
        style: {
          textAlign: "center"
        },
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Delivered Quantity",
        accessor: "delivered_quantity",
        aggregate: aggregateSum,
        Aggregated: ({ value }) => (
          <div title={value} data-bs-toggle="tooltip">
            <p className="text-center text-bold">{value}</p>
          </div>
        ),
        style: {
          textAlign: "center"
        },
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "UOM",
        accessor: "unit_measure_code",
        aggregate: aggregateValue,
        Aggregated: ({ value }) => <p className="text-center text-bold">{value}</p>,
        Cell: (props) => {
          return <p className="text-center">{props.value}</p>;
        }
      },
      {
        Header: "Date Needed",
        accessor: "date_needed",
        Cell: (props) => {
          return props.value ? (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{moment(props.value).format("MM/DD/YYYY")}</p>
            </div>
          ) : (
            <></>
          );
        }
      },
      {
        Header: "Supplier",
        accessor: "",
        style: {
          width: "17%"
        },
        Cell: (props) => {
          const isAggregate = props.row.canExpand;
          const purchaseRequestItem = props.row.original;
          const itemSuppliers = props.row.original?.item_suppliers || [];

          return isAggregate ? (
            ""
          ) : (
            <Row>
              <Col>
                <Controller
                  name={`data.${props.row.index}.item_supplier_id`}
                  control={control}
                  render={({ field }) => (
                    <ReactSelect
                      isSearchable
                      isClearable
                      options={itemSuppliers}
                      placeholder="Select a Supplier"
                      noOptionsMessage={() => "No available suppliers."}
                      getOptionLabel={(option) => option.supplier.name}
                      getOptionValue={(option) => option.id}
                      onChange={(itemSupplier, actionMeta) => {
                        field.onChange(itemSupplier?.id);

                        if (actionMeta.action == "select-option") {
                          const unitPrice = parseFloat(itemSupplier.current_price);
                          const requestedQuantity = getValues(`data.${props.row.index}.requested_quantity`);
                          if (requestedQuantity) {
                            const totalAmount = requestedQuantity * unitPrice;

                            setValue(`data.${props.row.index}.purchase_request_item_id`, purchaseRequestItem.id);
                            setValue(`data.${props.row.index}.supplier_id`, itemSupplier.supplier.id);
                            setValue(`data.${props.row.index}.unit_price`, itemSupplier.current_price);
                            setValue(`data.${props.row.index}.total_amount`, totalAmount.toString());
                          }
                        } else {
                          setValue(`data.${props.row.index}.purchase_request_item_id`, null);
                          setValue(`data.${props.row.index}.supplier_id`, null);
                          setValue(`data.${props.row.index}.unit_price`, null);
                          setValue(`data.${props.row.index}.total_amount`, null);
                          setValue(`data.${props.row.index}.item_supplier_id`, "");
                        }
                      }}
                      theme={(theme) => ({
                        ...theme,
                        borderRadius: 7,
                        colors: {
                          ...theme.colors,
                          primary50: "#00897b50",
                          primary25: "#00897b25",
                          primary: "#00897b"
                        },
                        spacing: { baseUnit: 5.5 }
                      })}
                      value={itemSuppliers.find((itemSupplier) => itemSupplier.id == field.value) || ""}
                    />
                  )}
                />
              </Col>
            </Row>
          );
        }
      },
      {
        Header: "Unit Price",
        accessor: "",
        Cell: (props) => {
          const isAggregate = props.row.canExpand;
          const unitPrice = watch(`data.${props.row.index}.unit_price`);

          return isAggregate ? (
            ""
          ) : unitPrice ? (
            <div title={formatter.format(parseFloat(unitPrice))} data-bs-toggle="tooltip">
              <p className="text-right text-bold text-truncate">{formatter.format(parseFloat(unitPrice))}</p>
            </div>
          ) : (
            <p className="text-center">{"-"}</p>
          );
        }
      },
      {
        Header: "Total Amount",
        accessor: "",
        Aggregated: ({ row }) => {
          const leafRows = row.leafRows;
          const leafTotalAmounts = leafRows.map((leafRow) => {
            const totalAmount = watch(`data.${leafRow.id}.total_amount`);
            return totalAmount ? totalAmount : "0.0";
          });
          const aggregatedTotal = aggregateSum(leafTotalAmounts);

          return aggregatedTotal ? (
            <div title={formatter.format(parseFloat(aggregatedTotal))} data-bs-toggle="tooltip">
              <p className="text-right text-bold text-truncate">{formatter.format(parseFloat(aggregatedTotal))}</p>
            </div>
          ) : (
            <p className="text-right text-bold text-truncate">{"₱0.0"}</p>
          );
        },
        Cell: (props) => {
          const isAggregate = props.row.canExpand;
          const totalAmount = watch(`data.${props.row.index}.total_amount`);

          return isAggregate ? (
            ""
          ) : totalAmount ? (
            <div title={formatter.format(parseFloat(totalAmount))} data-bs-toggle="tooltip">
              <p className="text-right text-bold text-truncate">{formatter.format(parseFloat(totalAmount))}</p>
            </div>
          ) : (
            <p className="text-center">{"-"}</p>
          );
        }
      },
      {
        Header: "View P.O",
        accessor: "",
        aggregate: aggregateValue,
        Aggregated: ({ value }) => <p className="text-center text-bold">{value}</p>,
        Cell: (props) => {
          const purchaseOrders = props.row.original.purchase_orders;
          const hasPurchaseOrders = purchaseOrders?.length > 0;

          return (
            <div className="d-flex justify-content-center">
              <button
                className="button button--action button--action-success"
                title={hasPurchaseOrders ? "View Purchase Orders" : "No Purchase Orders"}
                data-bs-toggle="tooltip"
                onClick={() => {
                  setSelectedPurchaseRequest(props.row.original.purchase_request);
                  setShowPurchaseOrdersModal(!showPurchaseOrdersModal);
                  setPurchaseOrders(purchaseOrders);
                }}
              >
                <SvgIcon name="clipboard-list" className="h5" />
              </button>
            </div>
          );
        }
      }
    ],
    []
  );

  const purchaseOrderColumns = React.useMemo(
    () => [
      {
        id: "id",
        Header: "Id",
        accessor: "id",
        Cell: () => {
          return <></>;
        }
      },
      {
        Header: "Purchase Order",
        accessor: "code",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Supplier",
        accessor: "supplier.name",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Ordered Quantity",
        accessor: "ordered_quantity",
        Cell: (props) => {
          return (
            <div title={props.value} data-bs-toggle="tooltip">
              <p className="text-center text-truncate">{props.value}</p>
            </div>
          );
        }
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: (props) => {
          const status = I18n.t(`activerecord.attributes.purchase_order.statuses.${props.value}`);

          return (
            <div
              className={`d-flex justify-content-center badge badge--pill badge--${props.value.replace("_", "-")}`}
              title={status}
              data-bs-toggle="tooltip"
            >
              <p className="text-truncate">{status}</p>
            </div>
          );
        }
      }
    ],
    []
  );

  const getPurchaseRequests = async () => {
    try {
      setLoading(true);
      const response = await api.get(purchase_requests_admin_material_reordering_index_path({ format: "json" }));
      setPurchaseRequestItems(response.data);
      return response.data;
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => {
    getPurchaseRequests()
      .then((result) => {
        setValue(
          "data",
          result.map((element) => {
            return {
              id: element.id,
              requested_quantity: element.requested_quantity
            };
          })
        );
      })
      .catch(console.error);
  }, []);

  const loader = () => {
    return (
      <div className="d-flex justify-content-center align-items-center h-100 pt-lg-8 pb-lg-8">
        <Spinner animation="border" role="status" size="lg" variant="brand">
          <span className="visually-hidden">Loading Item Inventories for Reordering...</span>
        </Spinner>
      </div>
    );
  };

  const noContent = () => {
    return (
      <Container fluid={true} className="text-center pt-lg-8 pb-lg-8">
        <Row className="mb-8">
          <Col>
            <SvgIcon name="purchases" className="display-icon text-gray" />
          </Col>
        </Row>
        <Row>
          <Col>
            <h5>No available material replenishment request.</h5>
          </Col>
        </Row>
      </Container>
    );
  };

  const dataFilter = () => {
    return (
      <Row className="mb-3">
        <Col>
          <Input placeholder="Search for Purchase Request Number" onChange={(e) => table.current.setFilter(1, e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Inventory ID" onChange={(e) => table.current.setFilter("item.code", e.target.value)} />
        </Col>
        <Col>
          <Input placeholder="Search for Inventory Name" onChange={(e) => table.current.setFilter(2, e.target.value)} />
        </Col>
        <Col>
          <Select
            placeholder="Filter by Warehouse"
            options={warehouses}
            isSearchable={false}
            getOptionLabel={(option) => option.name}
            getOptionValue={(option) => option.id}
            isOptionDisabled={(option) => !option.active}
            onChange={(newOption) => {
              if (newOption) {
                setWarehouseFilter(newOption.id);
                table.current.setFilter(4, newOption.id);
              } else {
                setWarehouseFilter("");
                table.current.setFilter(4, "");
              }
            }}
            value={warehouses.find((warehouse) => warehouse.id == warehouseFilter) || ""}
          />
        </Col>
        <Col sm="auto">
          <Button
            className="button--default"
            disabled={isSubmitting || isValidating}
            title="Refresh"
            icon="refresh"
            type="button"
            onClick={() => {
              getPurchaseRequests()
                .then((result) => {
                  setValue(
                    "data",
                    result.map((element) => {
                      return {
                        id: element.id,
                        requested_quantity: element.requested_quantity
                      };
                    })
                  );
                })
                .catch(console.error);
            }}
          />
        </Col>
      </Row>
    );
  };

  const dataTable = () => {
    return (
      <Row>
        <Col>
          <Table
            columns={columns}
            data={purchaseRequestItems}
            ref={table}
            setSkipPageReset={true}
            hiddenColumns={[1, 3, 4]}
            groupBy={["item.code"]}
          />
        </Col>
      </Row>
    );
  };

  const content = () => {
    return purchaseRequestItems.length > 0 ? (
      <>
        {dataFilter()}
        {dataTable()}
      </>
    ) : (
      noContent()
    );
  };

  React.useEffect(() => {
    const queryString = location.search;
    const urlParams = new URLSearchParams(queryString);
    const closed = urlParams.get("closed");

    if (closed && closed === "1") setTabIndex(1);
  }, []);

  return (
    <React.StrictMode>
      {formErrors && formErrors.length != 0 && (
        <Alert>
          {formErrors.map((error, index) => {
            return <div key={index}>{error}</div>;
          })}
        </Alert>
      )}
      <Tabs
        className="card"
        selectedTabClassName="project-tab__link project-tab__link--active"
        selectedIndex={tabIndex}
        onSelect={(index: number) => setTabIndex(index)}
      >
        <Container fluid={true}>
          <Row className="d-flex align-items-baseline">
            <Col>
              <TabList className="card__header" style={{ padding: 0 }}>
                <Col className="d-flex justify-content-start">
                  <Tab className="project-tab__link">Replenishment Requests</Tab>
                  <Tab className="project-tab__link">History</Tab>
                </Col>
              </TabList>
            </Col>
          </Row>
          <TabPanel>
            <Card.Header title="">
              {purchaseRequestItems.length > 0 && (
                <Button
                  className="button--brand"
                  icon="clipboard-list"
                  disabled={isSubmitting || isValidating}
                  title="Generate Purchase Orders"
                  type="button"
                  onClick={() => {
                    handleSubmit(async (data) => await save(data), onError)();
                  }}
                />
              )}
            </Card.Header>
            <Card.Body>{loading ? loader() : content()}</Card.Body>
          </TabPanel>
          <TabPanel>
            <MaterialReorderingRequestHistory warehouses={warehouses} hasPurchaseRequestHistory={has_purchase_request_history} />
          </TabPanel>
        </Container>
      </Tabs>
      <Modal dialogClassName="modal-dialog-centered" show={showPurchaseOrdersModal}>
        <Modal.Header>{`View Purchase Orders for: ${selectedPurchaseRequest?.code}`}</Modal.Header>
        <Modal.Body>
          {purchaseOrders.length > 0 ? (
            <>
              <Table columns={purchaseOrderColumns} data={purchaseOrders} />
            </>
          ) : (
            <div className="text-center mt-4 mb-4">No available purchase orders.</div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button
            onClick={() => {
              setShowPurchaseOrdersModal(!showPurchaseOrdersModal);
            }}
            icon="times"
            title="Close"
          />
        </Modal.Footer>
      </Modal>
    </React.StrictMode>
  );
};

export default MaterialReorderingRequests;
